/**
 * Canonical domain enums for Jalupro (Phase 1).
 *
 * These are the single source of truth. The Prisma schema mirrors these by name;
 * the web app imports the same string-literal unions for type-safe UI.
 *
 * Enums use `const` objects + derived union types (erasableSyntaxOnly-friendly,
 * no TS `enum` runtime quirks) so the same values are usable as values and types.
 */

/** Order lifecycle status. Happy path (Phase 1):
 *  NEW → PROFORMA_SENT → PAID → INVOICED → SHIPPED → DELIVERED.
 *  ON_HOLD_ADMIN_REVIEW is retained for FUTURE SEPA/postpay; NOT auto-triggered in Phase 1.
 *  SHIPPED may diverge to DELIVERY_PROBLEM.
 */
export const OrderStatus = {
  NEW: 'NEW',
  PROFORMA_SENT: 'PROFORMA_SENT',
  ON_HOLD_ADMIN_REVIEW: 'ON_HOLD_ADMIN_REVIEW',
  PAID: 'PAID',
  INVOICED: 'INVOICED',
  SHIPPED: 'SHIPPED',
  DELIVERED: 'DELIVERED',
  DELIVERY_PROBLEM: 'DELIVERY_PROBLEM',
} as const;
export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];

/** Office responsible for an order (matches the reference quote form's "Agente responsable"). */
export const ResponsibleOffice = {
  MADRID: 'MADRID',
  BARCELONA: 'BARCELONA',
  VALENCIA: 'VALENCIA',
  HEADQUARTER: 'HEADQUARTER',
} as const;
export type ResponsibleOffice = (typeof ResponsibleOffice)[keyof typeof ResponsibleOffice];

/** Payment terms. Phase 1 form exposes PREPAID only; the others are reserved for later phases. */
export const PaymentType = {
  PREPAID: 'PREPAID',
  /** Reserved — NOT offered in the Phase 1 order form. */
  POSTPAY_30D: 'POSTPAY_30D',
  /** Reserved/deferred — NOT exposed in Phase 1. */
  SEPA: 'SEPA',
} as const;
export type PaymentType = (typeof PaymentType)[keyof typeof PaymentType];

/** Payment method. Stripe/PayPal auto-confirm via webhook; IBAN is confirmed manually. */
export const PaymentMethod = {
  STRIPE: 'STRIPE',
  PAYPAL: 'PAYPAL',
  IBAN: 'IBAN',
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

/** Payment record status. */
export const PaymentStatus = {
  PENDING: 'PENDING',
  CONFIRMED: 'CONFIRMED',
  FAILED: 'FAILED',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

/**
 * User roles — the eight positions of JP-CRM Area 1 §4. What each may do is the
 * permission matrix in ./permissions.ts; nothing else holds role lists. The Agent has
 * their own portal (/agent); every other role shares the admin portal (/admin),
 * filtered by the matrix.
 */
export const UserRole = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ACCOUNTANT: 'ACCOUNTANT',
  BUSINESS_MANAGER: 'BUSINESS_MANAGER',
  SALES_MANAGER: 'SALES_MANAGER',
  PARTNER: 'PARTNER',
  ASSISTANT: 'ASSISTANT',
  WAREHOUSE: 'WAREHOUSE',
  AGENT: 'AGENT',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

/** User account status. JWT refresh is revoked on BLOCKED/delete. */
export const UserStatus = {
  ACTIVE: 'ACTIVE',
  BLOCKED: 'BLOCKED',
  INVITED: 'INVITED',
} as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

/** SEPA mandate lifecycle (deferred feature; enum kept for the future). */
export const SepaMandateStatus = {
  NONE: 'NONE',
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  REVOKED: 'REVOKED',
} as const;
export type SepaMandateStatus = (typeof SepaMandateStatus)[keyof typeof SepaMandateStatus];

/** Commercial condition of a client — chosen on the client record (one of three). */
export const CommercialCondition = {
  ESTANDAR: 'ESTANDAR',
  LARGO_PLAZO: 'LARGO_PLAZO',
  TRAINER: 'TRAINER',
} as const;
export type CommercialCondition = (typeof CommercialCondition)[keyof typeof CommercialCondition];

/** Source / channel of acquisition of a client (fixed list agreed with Jalupro). */
export const CLIENT_SOURCES = ['WORKSHOP', 'CONGRESS', 'REFERRAL', 'AGENT_VISIT', 'WEB', 'CAMPAIGN', 'OTHER'] as const;
export type ClientSource = (typeof CLIENT_SOURCES)[number];

/** Status of a billing entity's tax number (derived, never stored). */
export const TaxIdStatus = {
  /** Check digit valid (Spain), VIES-valid (other EU) or a national number outside the EU. */
  VALID: 'VALID',
  /** Check digit fails (Spain) or VIES says invalid (other EU). */
  INVALID: 'INVALID',
  /** No number at all. */
  MISSING: 'MISSING',
  /** EU number not yet confirmed by VIES (unverified / VIES unavailable). */
  UNVERIFIED: 'UNVERIFIED',
} as const;
export type TaxIdStatus = (typeof TaxIdStatus)[keyof typeof TaxIdStatus];

/** Client kind. A COMPANY may carry a VAT number (VIES-validated); a PERSON has only
 *  the fiscal ID (CIF/NIF/etc). Chosen on create/edit. */
export const ClientType = {
  COMPANY: 'COMPANY',
  PERSON: 'PERSON',
} as const;
export type ClientType = (typeof ClientType)[keyof typeof ClientType];

/** VAT-number validation state (companies only). Driven by VIES checks at create/edit and
 *  at proforma time; never blocks saving. */
export const VatStatus = {
  UNVERIFIED: 'UNVERIFIED',
  VALID: 'VALID',
  INVALID: 'INVALID',
  /** VIES (or that member state's service) was unreachable — retry later. */
  UNAVAILABLE: 'UNAVAILABLE',
  /** VAT is outside the EU VIES network — cannot be checked. */
  NOT_APPLICABLE: 'NOT_APPLICABLE',
} as const;
export type VatStatus = (typeof VatStatus)[keyof typeof VatStatus];

/** Shipment status mirrored from the carrier (Sendcloud). */
export const ShipmentStatus = {
  LABEL_CREATED: 'LABEL_CREATED',
  IN_TRANSIT: 'IN_TRANSIT',
  DELIVERED: 'DELIVERED',
  PROBLEM: 'PROBLEM',
} as const;
export type ShipmentStatus = (typeof ShipmentStatus)[keyof typeof ShipmentStatus];

/** Bank transaction reconciliation status (bank-feed go-live is deferred; enum kept). */
export const BankTransactionStatus = {
  UNMATCHED: 'UNMATCHED',
  SUGGESTED: 'SUGGESTED',
  MATCHED: 'MATCHED',
  IGNORED: 'IGNORED',
} as const;
export type BankTransactionStatus =
  (typeof BankTransactionStatus)[keyof typeof BankTransactionStatus];

/** Who/what performed an audited action. */
export const AuditActorType = {
  USER: 'USER',
  SYSTEM: 'SYSTEM',
  WEBHOOK: 'WEBHOOK',
  JOB: 'JOB',
} as const;
export type AuditActorType = (typeof AuditActorType)[keyof typeof AuditActorType];

/** Display tone for audit-log / timeline entries (drives UI styling). */
export const AuditTone = {
  INFO: 'INFO',
  SUCCESS: 'SUCCESS',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
} as const;
export type AuditTone = (typeof AuditTone)[keyof typeof AuditTone];

/** Fiscal/shipping document kinds. Fiscal docs are authored + numbered by Holded. */
export const DocumentType = {
  PROFORMA: 'PROFORMA',
  INVOICE: 'INVOICE',
  WAYBILL: 'WAYBILL',
} as const;
export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType];
